import React, { useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import {
  FiAlignCenter, FiAlignLeft, FiAlignRight, FiChevronLeft, FiChevronRight,
  FiChevronUp, FiChevronDown, FiCode, FiCopy, FiDownload, FiEye, FiEyeOff,
  FiMove, FiMaximize2, FiType, FiCrop, FiRotateCcw, FiX,
} from 'react-icons/fi';

import { storySafeAreaVars } from '../../../../config/storyCanvas';
import {
  RAIZ, caminhoDoElemento, estudioApi, listarElementos, patchCompleto,
  resolverElemento, rotuloDoElemento, useEstudio,
} from './shareStudio';
import Style from '../../../../styles/features/nutricao/ShareStudio.module.css';

// Padrões da zona útil (config/storyCanvas.js) já no espaço do cartão — servem
// de valor inicial dos controles da aba "Zona" enquanto ninguém mexeu neles.
const PADRAO_VARS = storySafeAreaVars();
const varPadrao = (nome) => parseFloat(PADRAO_VARS[nome]) || 0;

const ABAS = [
  { id: 'mover', rotulo: 'Mover', icone: <FiMove /> },
  { id: 'tamanho', rotulo: 'Tamanho', icone: <FiMaximize2 /> },
  { id: 'texto', rotulo: 'Texto', icone: <FiType /> },
  { id: 'zona', rotulo: 'Zona', icone: <FiCrop /> },
];

const CORES = ['#ffffff', '#f5c518', '#0b0d12', '#8b93a7', '#ff5a5f', '#3ddc97'];

const ALINHAMENTOS = [
  { id: 'left', rotulo: 'Alinhar à esquerda', icone: <FiAlignLeft /> },
  { id: 'center', rotulo: 'Centralizar', icone: <FiAlignCenter /> },
  { id: 'right', rotulo: 'Alinhar à direita', icone: <FiAlignRight /> },
];

// Tirar texto de dentro de uma WebView é traiçoeiro: `navigator.clipboard` só
// existe em contexto seguro (o Capacitor serve de https://localhost, então
// costuma ir, mas o Android antigo não tem), `execCommand` é o resgate velho e,
// no aparelho, a folha do sistema é a saída que nunca falha — dá para mandar o
// JSON para o WhatsApp ou as Notas e colar de lá. Três tentativas, em ordem.
const copiarTexto = async (texto) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(texto);
      return 'clipboard';
    }
  } catch { /* bloqueado — tenta o resgate */ }

  try {
    const area = document.createElement('textarea');
    area.value = texto;
    area.setAttribute('readonly', '');
    // Fora da tela, mas NÃO com display/visibility: o campo precisa poder
    // receber seleção para o execCommand ter o que copiar.
    area.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0';
    document.body.appendChild(area);
    area.select();
    area.setSelectionRange(0, texto.length);
    const copiou = document.execCommand('copy');
    area.remove();
    if (copiou) return 'clipboard';
  } catch { /* sem execCommand — tenta a folha */ }

  if (Capacitor.isNativePlatform()) {
    try {
      await Share.share({
        title: 'Ajustes das artes LesVita',
        text: texto,
        dialogTitle: 'Enviar o JSON dos ajustes',
      });
      return 'compartilhado';
    } catch { /* cancelou a folha */ }
  }

  return 'manual';
};

const arredondar = (n, casas = 2) => {
  const fator = 10 ** casas;
  return Math.round(Number(n) * fator) / fator;
};

// Linha de controle: rótulo, valor, dois passos finos e o slider. Os passos
// existem porque slider no dedo não acerta 1px — e 1px aqui vale 4px no PNG.
const Controle = ({ rotulo, valor, min, max, step = 1, sufixo = '', onChange, onReset }) => (
  <div className={Style.controle}>
    <div className={Style.controleTopo}>
      <span>{rotulo}</span>
      <div className={Style.controleValor}>
        <button type="button" onClick={() => onChange(arredondar(valor - step))} aria-label={`${rotulo} menos`}>−</button>
        <b>{arredondar(valor)}{sufixo}</b>
        <button type="button" onClick={() => onChange(arredondar(valor + step))} aria-label={`${rotulo} mais`}>+</button>
        {onReset && (
          <button type="button" className={Style.controleZerar} onClick={onReset} aria-label={`Zerar ${rotulo}`}>
            <FiRotateCcw />
          </button>
        )}
      </div>
    </div>
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={valor}
      onChange={(e) => onChange(arredondar(Number(e.target.value)))}
    />
  </div>
);

/**
 * Painel do estúdio. Fica FORA do cartão — nada daqui entra na imagem.
 *
 * @param {string} variante  id da arte em foco ('1k', '1a'…)
 * @param {HTMLElement} raiz nó `.shareCard` da arte em foco (o que vira o PNG)
 * @param {number} indice    posição da arte no carrossel (0-based)
 * @param {number} total     quantidade de artes
 * @param {(delta:number)=>void} onArte  navega no carrossel sem usar o dedo no
 *   cartão, que no modo estúdio está ocupado arrastando elemento.
 * @param {()=>void} onBaixar  gera e baixa o PNG sem sair do estúdio — é o
 *   arquivo que manda, e conferir nele é o ciclo de trabalho todo.
 * @param {boolean} baixando   captura em andamento
 */
const StudioPanel = ({ variante, raiz, indice, total, onArte, onBaixar, baixando }) => {
  const estudio = useEstudio();
  const [aba, setAba] = useState('mover');
  const [jsonAberto, setJsonAberto] = useState(false);
  const [rascunho, setRascunho] = useState('');
  // O texto da gaveta é um ESPELHO do estado enquanto ninguém digitar nele.
  // Sem isso ele congelava no momento em que a gaveta abriu, e o botão de
  // substituir devolvia esse retrato velho — engolindo tudo o que foi ajustado
  // depois (era isso que fazia a fonte 'sair' logo depois de aplicar).
  const [rascunhoEditado, setRascunhoEditado] = useState(false);
  const [aviso, setAviso] = useState('');

  const caminho = estudio.sel?.variante === variante ? estudio.sel.caminho : null;
  const patch = patchCompleto(estudio.ajustes[variante]?.[caminho]);
  const ehRaiz = caminho === RAIZ;

  // Mover/redimensionar o CARTÃO não faria sentido: ele é a moldura da imagem —
  // o html2canvas fotografa o próprio nó, então um deslocamento dele apareceria
  // na tela e sumiria no arquivo. No cartão só valem a zona útil e a cor.
  const abaVale = (id) => (id === 'zona' ? ehRaiz : (id === 'texto' || !ehRaiz));
  const abaAtiva = abaVale(aba) ? aba : (ehRaiz ? 'zona' : 'mover');

  // A lista é remontada quando a arte ou os ajustes mudam: esconder um elemento
  // muda o DOM e a navegação ◀ ▶ tem de acompanhar.
  const elementos = useMemo(
    () => listarElementos(raiz),
    // eslint-disable-next-line
    [raiz, variante, estudio.ajustes[variante]],
  );
  const selecionado = caminho ? resolverElemento(raiz, caminho) : null;
  const posicao = selecionado ? elementos.indexOf(selecionado) : -1;

  const mudar = (mudancas) => {
    if (!caminho) return;
    estudioApi.ajustar(variante, caminho, mudancas);
  };

  const irElemento = (delta) => {
    if (!elementos.length) return;
    const proximo = posicao < 0
      ? (delta > 0 ? 0 : elementos.length - 1)
      : (posicao + delta + elementos.length) % elementos.length;
    estudioApi.selecionar(variante, caminhoDoElemento(raiz, elementos[proximo]));
  };

  // Subir/descer no ANINHAMENTO. O dedo acerta o bloco, não o texto de 9px
  // dentro dele (o número no meio do anel, a linha "P 86g · C 131g"): tocar
  // pega o container e daqui se desce até o pedaço exato que precisa mover.
  const editavel = (el) => el && !el.closest('svg') && el.getAttribute('data-el') !== 'fundo';

  const irPai = () => {
    if (!selecionado || ehRaiz) return;
    const pai = selecionado.parentElement;
    estudioApi.selecionar(variante, pai && pai !== raiz ? caminhoDoElemento(raiz, pai) : RAIZ);
  };

  const irFilho = () => {
    const base = ehRaiz ? raiz : selecionado;
    const filho = Array.from(base?.children || []).find(editavel);
    if (filho) estudioApi.selecionar(variante, caminhoDoElemento(raiz, filho));
  };

  // Mantém a gaveta mostrando o estado de agora enquanto ninguém digitou nela.
  useEffect(() => {
    if (!jsonAberto || rascunhoEditado) return;
    setRascunho(estudioApi.exportarJson());
  }, [jsonAberto, rascunhoEditado, estudio.ajustes]);

  const varAtual = (nome) => {
    const bruto = patch.vars?.[nome];
    const lido = parseFloat(bruto);
    return Number.isFinite(lido) ? lido : varPadrao(nome);
  };

  const mudarVar = (nome, valor) => {
    const vars = { ...(patch.vars || {}) };
    if (valor === null) delete vars[nome];
    else vars[nome] = `${valor}px`;
    mudar({ vars: Object.keys(vars).length ? vars : null });
  };

  // Copia o JSON de TODAS as artes de uma vez (é isso que o `exportarJson`
  // devolve: o mapa inteiro, não só a arte em foco). Sempre abre a gaveta junto
  // — se as três tentativas de copiar falharem, o texto fica ali para pegar na
  // mão, e nunca se perde o trabalho por causa de um clipboard bloqueado.
  const copiarTudo = async () => {
    const texto = estudioApi.exportarJson();
    setRascunho(texto);
    setJsonAberto(true);
    const artes = Object.keys(JSON.parse(texto)).length;
    const tamanho = `${(new Blob([texto]).size / 1024).toFixed(1)} KB`;
    const resumo = `${artes} ${artes === 1 ? 'arte' : 'artes'}, ${tamanho}`;
    setRascunhoEditado(false);
    const via = await copiarTexto(texto);
    if (via === 'clipboard') setAviso(`JSON copiado (${resumo}) — cole em shareOverrides.js.`);
    else if (via === 'compartilhado') setAviso(`JSON enviado (${resumo}).`);
    else setAviso(`Não deu para copiar sozinho (${resumo}): selecione o texto abaixo.`);
  };

  const aplicarJson = () => {
    try {
      estudioApi.importarJson(rascunho);
      setRascunhoEditado(false);
      setAviso('JSON aplicado — ele substituiu TODOS os ajustes.');
    } catch (erro) {
      setAviso(`JSON inválido: ${erro.message}`);
    }
  };

  const semSelecao = !caminho;

  return (
    <section className={Style.painel} aria-label="Estúdio das artes">
      <header className={Style.cabecalho}>
        <strong>Estúdio das artes</strong>
        <button
          type="button"
          className={Style.chip}
          onClick={copiarTudo}
          title="Copiar o JSON de TODAS as artes ajustadas"
        >
          <FiCopy /> copiar tudo
        </button>
        <span className={Style.badge}>{variante}</span>
        <button
          type="button"
          className={Style.iconeBotao}
          onClick={onBaixar}
          disabled={baixando}
          title="Baixar a imagem gerada desta arte"
          aria-label="Baixar a imagem gerada"
        >
          <FiDownload />
        </button>
        <button
          type="button"
          className={Style.iconeBotao}
          onClick={() => estudioApi.desligar()}
          title="Fechar estúdio"
          aria-label="Fechar estúdio"
        >
          <FiX />
        </button>
      </header>

      <div className={Style.navegacao}>
        <div className={Style.navGrupo}>
          <button type="button" onClick={() => onArte(-1)} aria-label="Arte anterior"><FiChevronLeft /></button>
          <span>arte {indice + 1}/{total}</span>
          <button type="button" onClick={() => onArte(1)} aria-label="Próxima arte"><FiChevronRight /></button>
        </div>
        <button
          type="button"
          className={`${Style.chip} ${ehRaiz ? Style.chipOn : ''}`}
          onClick={() => estudioApi.selecionar(variante, RAIZ)}
        >
          cartão
        </button>
      </div>

      {/* ◀ ▶ andam entre os elementos na ordem da arte; ⌃ ⌄ sobem e descem no
          aninhamento — é assim que se chega no texto pequeno dentro de um bloco
          sem precisar acertá-lo com o dedo. */}
      <div className={Style.navegacao}>
        <div className={Style.navGrupo}>
          <button type="button" onClick={() => irElemento(-1)} aria-label="Elemento anterior"><FiChevronLeft /></button>
          <button type="button" onClick={irPai} disabled={semSelecao || ehRaiz} aria-label="Elemento pai"><FiChevronUp /></button>
          <span className={Style.navRotulo}>
            {semSelecao ? 'toque no cartão' : (ehRaiz ? 'cartão inteiro' : rotuloDoElemento(selecionado))}
          </span>
          <button type="button" onClick={irFilho} disabled={semSelecao} aria-label="Elemento filho"><FiChevronDown /></button>
          <button type="button" onClick={() => irElemento(1)} aria-label="Próximo elemento"><FiChevronRight /></button>
        </div>
      </div>

      <div className={Style.abas} role="tablist">
        {ABAS.map((item) => {
          const desabilitada = !abaVale(item.id);
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={abaAtiva === item.id}
              disabled={desabilitada}
              className={`${Style.aba} ${abaAtiva === item.id ? Style.abaOn : ''}`}
              onClick={() => setAba(item.id)}
            >
              {item.icone}<span>{item.rotulo}</span>
            </button>
          );
        })}
      </div>

      <div className={Style.corpo}>
        {semSelecao && (
          <p className={Style.dica}>
            Toque no cartão para escolher o elemento e arraste para posicionar;
            use ⌃ ⌄ para entrar num texto pequeno. Estes ajustes valem para a
            IMAGEM GERADA — com o estúdio fechado, a amostra na tela volta ao
            desenho original.
          </p>
        )}

        {!semSelecao && abaAtiva === 'mover' && (
          <>
            <Controle
              rotulo="Horizontal" valor={patch.dx} min={-135} max={135} sufixo="px"
              onChange={(v) => mudar({ dx: v })} onReset={() => mudar({ dx: 0 })}
            />
            <Controle
              rotulo="Vertical" valor={patch.dy} min={-240} max={240} sufixo="px"
              onChange={(v) => mudar({ dy: v })} onReset={() => mudar({ dy: 0 })}
            />
            <Controle
              rotulo="Girar" valor={patch.giro} min={-180} max={180} sufixo="°"
              onChange={(v) => mudar({ giro: v })} onReset={() => mudar({ giro: 0 })}
            />
            <p className={Style.dica}>
              Aqui anda a CAIXA inteira. Para mexer só no texto dentro dela,
              vá na aba Texto.
            </p>
          </>
        )}

        {!semSelecao && abaAtiva === 'tamanho' && (
          <>
            <Controle
              rotulo="Escala" valor={patch.escala} min={0.3} max={2.5} step={0.01}
              sufixo="×" onChange={(v) => mudar({ escala: v })} onReset={() => mudar({ escala: 1 })}
            />
            <Controle
              rotulo={patch.largura ? 'Largura' : 'Largura (automática)'}
              valor={patch.largura || Math.round(selecionado?.offsetWidth || 100)}
              min={16} max={270} sufixo="px"
              onChange={(v) => mudar({ largura: v })} onReset={() => mudar({ largura: 0 })}
            />
            <Controle
              rotulo={patch.altura ? 'Altura' : 'Altura (automática)'}
              valor={patch.altura || Math.round(selecionado?.offsetHeight || 40)}
              min={8} max={480} sufixo="px"
              onChange={(v) => mudar({ altura: v })} onReset={() => mudar({ altura: 0 })}
            />
            <Controle
              rotulo="Opacidade" valor={patch.opacidade} min={0} max={1} step={0.05}
              onChange={(v) => mudar({ opacidade: v })} onReset={() => mudar({ opacidade: 1 })}
            />
            <button
              type="button"
              className={`${Style.chip} ${patch.esconder ? Style.chipOn : ''}`}
              onClick={() => mudar({ esconder: !patch.esconder })}
            >
              {patch.esconder ? 'elemento escondido' : 'esconder elemento'}
            </button>
          </>
        )}

        {!semSelecao && abaAtiva === 'texto' && (
          <>
            {/* Alinhar mexe em text-align E, quando a caixa é flex, no
                justify/align correspondente — ver aplicarPatch. */}
            <div className={Style.cores}>
              {ALINHAMENTOS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`${Style.chip} ${patch.alinhar === item.id ? Style.chipOn : ''}`}
                  onClick={() => mudar({ alinhar: patch.alinhar === item.id ? '' : item.id })}
                  aria-label={item.rotulo}
                  title={item.rotulo}
                >
                  {item.icone}
                </button>
              ))}
              <button type="button" className={Style.chip} onClick={() => mudar({ alinhar: '' })}>
                alinhamento do tema
              </button>
            </div>

            <Controle
              rotulo={patch.fonte ? 'Tamanho' : 'Tamanho (automático)'}
              valor={patch.fonte || Math.round(parseFloat(selecionado ? window.getComputedStyle(selecionado).fontSize : 14) || 14)}
              min={5} max={72} sufixo="px"
              onChange={(v) => mudar({ fonte: v })} onReset={() => mudar({ fonte: 0 })}
            />
            <Controle
              rotulo={patch.peso ? 'Peso' : 'Peso (automático)'}
              valor={patch.peso || Math.round(parseFloat(selecionado ? window.getComputedStyle(selecionado).fontWeight : 600) || 600)}
              min={300} max={900} step={100}
              onChange={(v) => mudar({ peso: v })} onReset={() => mudar({ peso: 0 })}
            />
            <Controle
              rotulo={patch.entrelinha ? 'Entrelinha' : 'Entrelinha (automática)'}
              valor={patch.entrelinha || 1.2} min={0.8} max={2} step={0.05} sufixo="×"
              onChange={(v) => mudar({ entrelinha: v })} onReset={() => mudar({ entrelinha: 0 })}
            />
            <Controle
              rotulo="Entre letras" valor={patch.entreletras} min={-3} max={8} step={0.1} sufixo="px"
              onChange={(v) => mudar({ entreletras: v })} onReset={() => mudar({ entreletras: 0 })}
            />

            <p className={Style.dica}>
              Abaixo move só o TEXTO dentro da caixa, sem levar a caixa junto —
              é o que desce o “P 86g” dentro da pílula. Funciona trocando o
              respiro de cima pelo de baixo; caixa sem respiro nenhum cresce.
            </p>
            <Controle
              rotulo="Texto ↕" valor={patch.textoY} min={-24} max={24} sufixo="px"
              onChange={(v) => mudar({ textoY: v })} onReset={() => mudar({ textoY: 0 })}
            />
            <Controle
              rotulo="Texto ↔" valor={patch.textoX} min={-24} max={24} sufixo="px"
              onChange={(v) => mudar({ textoX: v })} onReset={() => mudar({ textoX: 0 })}
            />

            <div className={Style.cores}>
              {CORES.map((cor) => (
                <button
                  key={cor}
                  type="button"
                  className={`${Style.amostra} ${patch.cor === cor ? Style.amostraOn : ''}`}
                  style={{ background: cor }}
                  onClick={() => mudar({ cor })}
                  aria-label={`Cor ${cor}`}
                />
              ))}
              <label className={Style.amostraLivre}>
                <input
                  type="color"
                  value={/^#[0-9a-f]{6}$/i.test(patch.cor) ? patch.cor : '#ffffff'}
                  onChange={(e) => mudar({ cor: e.target.value })}
                />
                <span>livre</span>
              </label>
              <button type="button" className={Style.chip} onClick={() => mudar({ cor: '' })}>
                cor do tema
              </button>
            </div>
          </>
        )}

        {ehRaiz && abaAtiva === 'zona' && (
          <>
            <p className={Style.dica}>
              Margens da zona útil do Story: o Instagram cobre o topo com a barra
              de progresso e a base com a caixa de resposta. Vale só para esta arte.
            </p>
            <Controle
              rotulo="Margem topo" valor={varAtual('--story-safe-top')} min={0} max={140} sufixo="px"
              onChange={(v) => mudarVar('--story-safe-top', v)}
              onReset={() => mudarVar('--story-safe-top', null)}
            />
            <Controle
              rotulo="Margem base" valor={varAtual('--story-safe-bottom')} min={0} max={140} sufixo="px"
              onChange={(v) => mudarVar('--story-safe-bottom', v)}
              onReset={() => mudarVar('--story-safe-bottom', null)}
            />
            <Controle
              rotulo="Margem lateral" valor={varAtual('--story-safe-x')} min={0} max={70} sufixo="px"
              onChange={(v) => mudarVar('--story-safe-x', v)}
              onReset={() => mudarVar('--story-safe-x', null)}
            />
          </>
        )}
      </div>

      <footer className={Style.rodape}>
        {/* Rótulo por extenso, não um olho solto: desligado aqui a arte volta ao
            original na hora — é botão de COMPARAR, e já foi confundido com
            "salvar". */}
        <button
          type="button"
          className={`${Style.chip} ${estudio.aplicar ? Style.chipOn : ''}`}
          onClick={() => estudioApi.aplicar(!estudio.aplicar)}
          aria-pressed={estudio.aplicar}
          title="Liga e desliga os ajustes para comparar com o original. Não apaga nada."
        >
          {estudio.aplicar ? <FiEye /> : <FiEyeOff />}
          {estudio.aplicar ? 'ajustes ligados' : 'ajustes desligados'}
        </button>
        <button
          type="button"
          className={Style.chip}
          disabled={semSelecao}
          onClick={() => estudioApi.limparElemento(variante, caminho)}
        >
          zerar elemento
        </button>
        <button type="button" className={Style.chip} onClick={() => estudioApi.limparCard(variante)}>
          zerar arte
        </button>
        <button
          type="button"
          className={`${Style.chip} ${jsonAberto ? Style.chipOn : ''}`}
          onClick={() => { setRascunho(estudioApi.exportarJson()); setRascunhoEditado(false); setJsonAberto((v) => !v); }}
        >
          <FiCode /> JSON
        </button>
      </footer>

      {jsonAberto && (
        <div className={Style.json}>
          <textarea
            value={rascunho}
            onChange={(e) => { setRascunho(e.target.value); setRascunhoEditado(true); setAviso(''); }}
            spellCheck={false}
            rows={6}
            aria-label="JSON dos ajustes"
          />
          <div className={Style.jsonAcoes}>
            <button type="button" className={Style.chip} onClick={copiarTudo}><FiCopy /> copiar</button>
            <button
              type="button"
              className={Style.chip}
              onClick={aplicarJson}
              disabled={!rascunhoEditado}
              title="Substitui TODOS os ajustes pelo texto acima"
            >
              substituir pelo texto
            </button>
            <button type="button" className={Style.chip} onClick={() => { estudioApi.restaurarPadrao(); setRascunho(estudioApi.exportarJson()); setAviso('Voltou ao padrão do código.'); }}>
              padrão do código
            </button>
            <button type="button" className={Style.chip} onClick={() => { estudioApi.limparTudo(); setRascunho('{}'); setAviso('Tudo zerado.'); }}>
              zerar tudo
            </button>
          </div>
          {aviso && <p className={Style.aviso}>{aviso}</p>}
        </div>
      )}
    </section>
  );
};

export default StudioPanel;
